import { io, Socket } from 'socket.io-client';
import { getApiUrl } from './env';
import { storage } from './storage';

interface WebSocketEvents {
  'connected': (data: { success: boolean; userId: string; userType: string }) => void;
  'subscribed': (data: { success: boolean; subscribed: string; timestamp: string }) => void;
  'jackpot:update': (data: { id: number; name: string; current_amount: number; currency_type: string }) => void;
  'jackpot:win': (data: { id: string; jackpot_tier: string; amount: number; currency_type: string; username?: string; is_me?: boolean }) => void;
  'balance:update': (data: { gc_amount: number; sc_bonus_amount: number; sc_promotional_amount: number; sc_redeemable_amount: number; transaction_type?: string; description?: string }) => void;
  'promotion:update': (data: any) => void;
  'latest-winner:new': (data: { id: string; playerName: string; gameName: string; gameImage: string; winAmount: number; currency: 'GC' | 'SC'; spinAmount: number; winType: string | null; timestamp: Date; gameId: number | null; gameExternalId: string | null; externalSessionId: string | null }) => void;
  'session:invalidated': (data: { message: string; timestamp: string }) => void;
  // Game session ended (user started a new game in another tab/device)
  'game:session:ended': (data: { message: string; endedSessionCount: number; timestamp: string }) => void;
  // SC max cap notification
  'sc_max_cap:applied': (data: { originalAmount: number; cappedAmount: number; scMaxCap: number; stateName: string; stateCode: string }) => void;
  // Cache invalidation events (triggered by admin actions)
  'vip:updated': (data: { tierName: string; tierLevel: number; previousTierName: string; previousTierLevel: number; isManualUpgrade?: boolean }) => void;
  'profile:updated': (data: { type: string; status: string }) => void;
  // Challenge events
  'challenge:progress': (data: { challengeId: number; progress: number; target: number; percentComplete: number; isCompleted: boolean }) => void;
  'challenge:completed': (data: { challengeId: number }) => void;
  'challenge:score': (data: { challengeId: number; score: number; peakScore: number; rank: number }) => void;
  'challenge:lobby': (data: { challengeId: number; lobbyStatus: string; participantCount: number; maxParticipants: number }) => void;
  'challenge:settled': (data: { challengeId: number; winnerUsername: string; payoutAmount: number; currencyType: string; finalLeaderboard: any[] }) => void;
  // Maintenance mode
  'maintenance:update': (data: { enabled: boolean; timestamp: string }) => void;
  // Payment methods updated (admin changed processor config)
  'payment:methods:updated': (data: { timestamp: string }) => void;
}

class WebSocketClient {
  private socket: Socket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private listeners: Map<string, Set<Function>> = new Map();
  private isConnecting = false;
  private activeChallengeRooms: Set<number> = new Set();
  // Track pending subscriptions to avoid duplicate subscription attempts
  private pendingSubscriptions: Set<string> = new Set();
  // Track confirmed subscriptions
  private confirmedSubscriptions: Set<string> = new Set();

  connect(token: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.socket?.connected) {
        resolve();
        return;
      }

      if (this.isConnecting) {
        // Wait for existing connection attempt
        const checkInterval = setInterval(() => {
          if (!this.isConnecting) {
            clearInterval(checkInterval);
            if (this.socket?.connected) {
              resolve();
            } else {
              reject(new Error('Connection failed'));
            }
          }
        }, 100);
        return;
      }

      this.isConnecting = true;

      // Use the same environment detection as API client
      // Get API URL (no prefix needed)
      const apiUrl = getApiUrl();
      
      // Use API URL directly as backend URL
      let backendUrl = apiUrl;
      
      // If VITE_BACKEND_URL is explicitly set, use it (for WebSocket-specific config)
      if (import.meta.env.VITE_BACKEND_URL) {
        backendUrl = import.meta.env.VITE_BACKEND_URL;
      }
      
      // Extract host and determine protocol
      const wsUrl = backendUrl.replace(/^https?:\/\//, '').split('/')[0];
      const protocol = backendUrl.startsWith('https') ? 'wss' : 'ws';

      // Safari ITP blocks third-party cookies (like ALB's AWSALB sticky session cookie)
      // when frontend (www.goldmachine.com) and API (api.goldmachine.com) are on different subdomains.
      // Polling transport requires sticky sessions to work across multiple backend instances.
      // WebSocket transport maintains a persistent connection to a single instance, so it doesn't need sticky sessions.
      // Force WebSocket-only for Safari to avoid the polling + blocked cookie issue.
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      const transports: ('websocket' | 'polling')[] = isSafari ? ['websocket'] : ['websocket', 'polling'];
      
      this.socket = io(`${protocol}://${wsUrl}/realtime`, {
        auth: {
          token,
        },
        query: {
          token,
        },
        transports,
        reconnection: true,
        reconnectionDelay: this.reconnectDelay,
        reconnectionAttempts: this.maxReconnectAttempts,
        reconnectionDelayMax: 5000,
        timeout: 20000,
        forceNew: false,
        upgrade: true,
        rememberUpgrade: false,
        autoConnect: true,
      });

      this.socket.on('connect', () => {
        this.reconnectAttempts = 0;
        this.isConnecting = false;
        // Don't subscribe here - wait for 'connected' event (after authentication)
        resolve();
      });

      this.socket.on('connected', () => {
        // Clear confirmed subscriptions on new connection - they need to be re-established
        this.confirmedSubscriptions.clear();
        // Subscribe after authentication is confirmed
        this.resubscribeAll();
      });

      this.socket.on('error', (error: { message?: string; code?: string }) => {
        this.isConnecting = false;
        
        // If it's an authentication error, don't try to reconnect
        if (error.code === 'TOKEN_EXPIRED' || error.code === 'AUTH_FAILED' || 
            error.message?.includes('token') || error.message?.includes('auth')) {
          this.reconnectAttempts = this.maxReconnectAttempts;
          reject(new Error(error.message || 'Authentication failed'));
        }
      });

      this.socket.on('disconnect', (reason) => {
        this.isConnecting = false;
        // Clear confirmed subscriptions on disconnect
        this.confirmedSubscriptions.clear();
        this.pendingSubscriptions.clear();
        
        // For server-initiated disconnects, attempt manual reconnect
        if (reason === 'io server disconnect') {
          const token = storage.getItem('auth_token') || storage.getItem('token');
          if (token && this.reconnectAttempts < this.maxReconnectAttempts) {
            setTimeout(() => {
              this.reconnect();
            }, 2000);
          }
        }
        // For other disconnect reasons (network issues, etc.), let Socket.IO handle reconnection
      });

      this.socket.on('connect_error', (error) => {
        this.isConnecting = false;
        this.reconnectAttempts++;
        
        // Don't reject on connect_error if reconnection is enabled
        // Socket.IO will handle reconnection automatically
        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
          reject(error);
        }
      });

      // Handle reconnection events
      this.socket.on('reconnect', () => {
        this.reconnectAttempts = 0;
        // Clear and re-establish subscriptions after reconnection
        this.confirmedSubscriptions.clear();
        this.pendingSubscriptions.clear();
        // The 'connected' event will fire after reconnect and trigger resubscribeAll
      });

      this.socket.on('reconnect_attempt', () => {
        // Reconnection attempt in progress
      });

      this.socket.on('reconnect_failed', () => {
        this.reconnectAttempts = this.maxReconnectAttempts;
      });

      // Handle transport upgrade (polling -> websocket)
      // Re-subscribe after upgrade to ensure subscriptions are maintained
      this.socket.io?.engine?.on('upgrade', () => {
        this.resubscribeAll();
      });

      // Forward all events to registered listeners
      this.setupEventForwarding();
    });
  }

  private setupEventForwarding() {
    if (!this.socket) return;

    const events: (keyof WebSocketEvents)[] = [
      'connected',
      'subscribed',
      'jackpot:update',
      'jackpot:win',
      'balance:update',
      'promotion:update',
      'latest-winner:new',
      'session:invalidated',
      'game:session:ended',
      'sc_max_cap:applied',
      'vip:updated',
      'profile:updated',
      'challenge:progress',
      'challenge:completed',
      'challenge:score',
      'challenge:lobby',
      'challenge:settled',
      'maintenance:update',
      'payment:methods:updated',
    ];

    events.forEach((event) => {
      this.socket?.on(event, (data) => {
        const listeners = this.listeners.get(event);
        if (listeners) {
          listeners.forEach((listener) => {
            try {
              listener(data);
            } catch {
              // Error in listener
            }
          });
        }
      });
    });
  }

  subscribe(event: string, callback: Function) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)?.add(callback);

    // Map event names to subscription messages
    // Some events require multiple room subscriptions (e.g., jackpot:win is sent to both
    // the public jackpot-wins room and the user's private balance room for is_me=true wins)
    const getSubscriptionMessages = (evt: string): string[] => {
      if (evt === 'jackpot:update') return ['subscribe:jackpots'];
      // jackpot:win needs both rooms: public wins go to jackpot-wins, personal wins go to balance
      if (evt === 'jackpot:win') return ['subscribe:jackpot-wins', 'subscribe:balance'];
      if (evt === 'balance:update' || evt === 'sc_max_cap:applied') return ['subscribe:balance'];
      if (evt === 'promotion:update') return ['subscribe:promotions'];
      if (evt === 'latest-winner:new') return ['subscribe:latest-winners'];
      return [];
    };

    const subscriptionMessages = getSubscriptionMessages(event);
    if (subscriptionMessages.length === 0) {
      // No room subscription needed for this event
      return () => this.unsubscribe(event, callback);
    }

    // Subscribe to the room(s) with acknowledgment confirmation
    const subscribeToRoom = (subscriptionMessage: string) => {
      if (!this.socket?.connected) {
        return;
      }

      // Skip if already confirmed or pending
      if (this.confirmedSubscriptions.has(subscriptionMessage) || 
          this.pendingSubscriptions.has(subscriptionMessage)) {
        return;
      }

      this.pendingSubscriptions.add(subscriptionMessage);

      // Use callback-based emit for acknowledgment (works with all Socket.IO versions)
      this.socket.emit(subscriptionMessage, (response: { success: boolean; subscribed?: string; error?: string }) => {
        this.pendingSubscriptions.delete(subscriptionMessage);
        
        if (response?.success) {
          this.confirmedSubscriptions.add(subscriptionMessage);
        } else {
          // Subscription failed - retry once after a short delay
          setTimeout(() => {
            if (this.socket?.connected && !this.confirmedSubscriptions.has(subscriptionMessage)) {
              this.socket.emit(subscriptionMessage, (retryResponse: { success: boolean }) => {
                if (retryResponse?.success) {
                  this.confirmedSubscriptions.add(subscriptionMessage);
                }
              });
            }
          }, 500);
        }
      });
    };

    // If socket is already connected, subscribe immediately to all required rooms
    if (this.socket?.connected) {
      subscriptionMessages.forEach(subscribeToRoom);
    }
    // Otherwise, resubscribeAll will be called when 'connected' event fires

    return () => this.unsubscribe(event, callback);
  }

  /**
   * Join a challenge-specific WebSocket room for real-time score/lobby/settlement updates.
   * Returns a cleanup function to leave the room.
   */
  joinChallengeRoom(challengeId: number): () => void {
    this.activeChallengeRooms.add(challengeId);
    const roomKey = `challenge:${challengeId}`;

    const emitJoin = () => {
      if (!this.socket?.connected) {
        return;
      }

      // Skip if already confirmed or pending
      if (this.confirmedSubscriptions.has(roomKey) || this.pendingSubscriptions.has(roomKey)) {
        return;
      }

      this.pendingSubscriptions.add(roomKey);
      this.socket.emit('subscribe:challenge', { challengeId }, (response: { success: boolean }) => {
        this.pendingSubscriptions.delete(roomKey);
        if (response?.success) {
          this.confirmedSubscriptions.add(roomKey);
        } else {
          // Retry once on failure
          setTimeout(() => {
            if (this.socket?.connected && !this.confirmedSubscriptions.has(roomKey)) {
              this.socket.emit('subscribe:challenge', { challengeId }, (retryResponse: { success: boolean }) => {
                if (retryResponse?.success) {
                  this.confirmedSubscriptions.add(roomKey);
                }
              });
            }
          }, 500);
        }
      });
    };

    if (this.socket?.connected) {
      emitJoin();
    }
    // Otherwise, resubscribeAll will handle it when 'connected' event fires

    return () => this.leaveChallengeRoom(challengeId);
  }

  /**
   * Leave a challenge-specific WebSocket room.
   */
  leaveChallengeRoom(challengeId: number) {
    this.activeChallengeRooms.delete(challengeId);
    const roomKey = `challenge:${challengeId}`;
    this.confirmedSubscriptions.delete(roomKey);
    this.pendingSubscriptions.delete(roomKey);
    
    if (this.socket?.connected) {
      this.socket.emit('unsubscribe:challenge', { challengeId });
    }
  }

  unsubscribe(event: string, callback: Function) {
    const listeners = this.listeners.get(event);
    if (listeners) {
      listeners.delete(callback);
      if (listeners.size === 0) {
        this.listeners.delete(event);
      }
    }
  }

  private resubscribeAll() {
    // Re-subscribe to all active event listeners when connection is established
    if (!this.socket?.connected) {
      return;
    }

    // Map event names to subscription messages
    // Some events require multiple room subscriptions (e.g., jackpot:win is sent to both
    // the public jackpot-wins room and the user's private balance room for is_me=true wins)
    const getSubscriptionMessages = (evt: string): string[] => {
      if (evt === 'jackpot:update') return ['subscribe:jackpots'];
      // jackpot:win needs both rooms: public wins go to jackpot-wins, personal wins go to balance
      if (evt === 'jackpot:win') return ['subscribe:jackpot-wins', 'subscribe:balance'];
      if (evt === 'balance:update' || evt === 'sc_max_cap:applied') return ['subscribe:balance'];
      if (evt === 'promotion:update') return ['subscribe:promotions'];
      if (evt === 'latest-winner:new') return ['subscribe:latest-winners'];
      return [];
    };

    // Collect unique subscription messages needed
    const subscriptionsNeeded = new Set<string>();
    this.listeners.forEach((callbacks, event) => {
      if (callbacks.size > 0) {
        const msgs = getSubscriptionMessages(event);
        msgs.forEach(msg => subscriptionsNeeded.add(msg));
      }
    });

    // Subscribe to each room with acknowledgment
    subscriptionsNeeded.forEach((subscriptionMessage) => {
      // Skip if already confirmed or pending
      if (this.confirmedSubscriptions.has(subscriptionMessage) || 
          this.pendingSubscriptions.has(subscriptionMessage)) {
        return;
      }

      this.pendingSubscriptions.add(subscriptionMessage);

      this.socket?.emit(subscriptionMessage, (response: { success: boolean; subscribed?: string; error?: string }) => {
        this.pendingSubscriptions.delete(subscriptionMessage);
        
        if (response?.success) {
          this.confirmedSubscriptions.add(subscriptionMessage);
        } else {
          // Subscription failed - retry once
          setTimeout(() => {
            if (this.socket?.connected && !this.confirmedSubscriptions.has(subscriptionMessage)) {
              this.socket.emit(subscriptionMessage, (retryResponse: { success: boolean }) => {
                if (retryResponse?.success) {
                  this.confirmedSubscriptions.add(subscriptionMessage);
                }
              });
            }
          }, 500);
        }
      });
    });

    // Re-join challenge rooms with acknowledgment
    this.activeChallengeRooms.forEach((challengeId) => {
      const roomKey = `challenge:${challengeId}`;
      if (this.confirmedSubscriptions.has(roomKey) || this.pendingSubscriptions.has(roomKey)) {
        return;
      }

      this.pendingSubscriptions.add(roomKey);
      this.socket?.emit('subscribe:challenge', { challengeId }, (response: { success: boolean }) => {
        this.pendingSubscriptions.delete(roomKey);
        if (response?.success) {
          this.confirmedSubscriptions.add(roomKey);
        }
      });
    });
  }

  private reconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      return;
    }

    // Get token from the correct localStorage key
    const token = storage.getItem('auth_token') || storage.getItem('token') || '';
    if (!token) {
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);

    setTimeout(() => {
      if (this.socket && !this.socket.connected) {
        this.connect(token).catch((error) => {
          // If it's an authentication error, don't keep trying
          if (error?.message?.includes('auth') || error?.message?.includes('token')) {
            this.reconnectAttempts = this.maxReconnectAttempts;
          }
        });
      }
    }, delay);
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    this.listeners.clear();
    this.confirmedSubscriptions.clear();
    this.pendingSubscriptions.clear();
    this.reconnectAttempts = 0;
  }

  isConnected(): boolean {
    return this.socket?.connected || false;
  }
}

export const websocketClient = new WebSocketClient();

